import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import axios from 'axios';
import { cn } from '../lib/utils';
import { 
  Send, 
  Loader2, 
  Sparkles, 
  User, 
  X, 
  Minus,
  MapPin,
  CheckCircle2,
  Calendar,
  Clock,
  Users
} from 'lucide-react';

const CaddieHat = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 16s0-4 8-4 8 4 8 4" />
    <path d="M2 16h20" />
    <ellipse cx="12" cy="12" rx="6" ry="4" />
    <path d="M6 12c0-3.3 2.7-6 6-6s6 2.7 6 6" />
  </svg>
);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LOCATION_KEYWORDS = ['near', 'nearby', 'close', 'around', 'find', 'where', 'local', 'location', 'closest', 'my area'];

const BookingCard = ({ booking }) => (
  <div className="bg-green-50 border border-green-200 rounded-lg p-3 my-2" data-testid="booking-confirmation-card">
    <div className="flex items-center gap-2 mb-2">
      <CheckCircle2 className="w-4 h-4 text-green-600" />
      <span className="text-green-800 font-semibold text-sm">Tee Time Confirmed</span>
    </div>
    <div className="space-y-1 text-xs text-green-700">
      <div className="flex items-center gap-2">
        <MapPin className="w-3 h-3" />
        <span className="font-medium">{booking.course_name}</span>
      </div>
      <div className="flex items-center gap-2">
        <Calendar className="w-3 h-3" />
        <span>{new Date(booking.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
      </div>
      <div className="flex items-center gap-2">
        <Clock className="w-3 h-3" />
        <span>{booking.time}</span>
      </div>
      <div className="flex items-center gap-2">
        <Users className="w-3 h-3" />
        <span>{booking.players} player{booking.players > 1 ? 's' : ''}</span>
      </div>
    </div>
    <p className="text-xs text-green-600 mt-2 italic">Ref: {booking.booking_id?.slice(0, 8)}</p>
  </div>
);

const CaddieWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm Caddie, your personal golf concierge. I can find courses near you, book tee times, and help plan your next round. What can I help you with?"
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState('idle');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const requestLocation = useCallback(() => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        setLocationStatus('denied');
        resolve(null);
        return;
      }
      setLocationStatus('requesting');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          setUserLocation(loc);
          setLocationStatus('granted');
          resolve(loc);
        },
        () => {
          setLocationStatus('denied');
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
      );
    });
  }, []);

  const needsLocation = (text) => {
    const lower = text.toLowerCase();
    return LOCATION_KEYWORDS.some(kw => lower.includes(kw));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    let loc = userLocation;

    if (needsLocation(userMessage) && !loc) {
      loc = await requestLocation();
    }

    try {
      const payload = { query: userMessage };
      if (loc) {
        payload.latitude = loc.latitude;
        payload.longitude = loc.longitude;
      }
      const response = await axios.post(`${API_URL}/caddie/query`, payload);
      
      // Add AI response
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
      
      // If a booking was made, add a booking card
      if (response.data.booking) {
        setMessages(prev => [...prev, { role: 'booking', booking: response.data.booking }]);
      }
    } catch {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, I couldn't process that. Please try again."
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleShareLocation = async () => {
    const loc = await requestLocation();
    if (loc) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Got your location! I can now find courses near you and help book tee times. What are you looking for?` 
      }]);
    } else {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I couldn't access your location. No worries — just tell me what city or area you'd like to play in and I'll find the best options!" 
      }]);
    }
  };

  const quickQuestions = [
    "Book a tee time",
    "Find courses near me",
    "Upcoming events"
  ];

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "fixed bottom-6 right-6 z-[9999] w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110",
          open 
            ? "bg-deep-green hover:bg-deep-green/90" 
            : "bg-gold hover:bg-gold/90 animate-bounce-subtle"
        )}
        data-testid="caddie-floating-btn"
        aria-label="Open Caddie AI Assistant"
      >
        {open ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <CaddieHat className="w-7 h-7 text-deep-green" />
        )}
      </button>

      {/* Chat Panel */}
      {open && (
        <div 
          className="fixed bottom-24 right-6 z-[9998] w-[380px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-8rem)] bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col overflow-hidden animate-slide-up"
          data-testid="caddie-widget-panel"
        >
          {/* Header */}
          <div className="bg-deep-green px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gold flex items-center justify-center">
                <CaddieHat className="w-5 h-5 text-deep-green" />
              </div>
              <div>
                <h3 className="text-white font-display font-semibold text-sm">Caddie</h3>
                <div className="flex items-center gap-1.5">
                  <p className="text-cream/60 text-xs">Golf Concierge</p>
                  {locationStatus === 'granted' && (
                    <span className="flex items-center gap-0.5 text-green-400 text-xs">
                      <MapPin className="w-2.5 h-2.5" /> GPS
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/60 hover:text-white transition-colors p-1"
              data-testid="caddie-widget-minimize"
            >
              <Minus className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-3">
            <div className="space-y-3">
              {messages.map((msg, i) => {
                // Booking confirmation card
                if (msg.role === 'booking') {
                  return <BookingCard key={i} booking={msg.booking} />;
                }
                
                return (
                  <div
                    key={i}
                    className={cn(
                      "flex gap-2",
                      msg.role === 'user' && "flex-row-reverse"
                    )}
                  >
                    <div className={cn(
                      "w-7 h-7 rounded-full flex items-center justify-center shrink-0",
                      msg.role === 'assistant' ? "bg-gold/20" : "bg-fairway/20"
                    )}>
                      {msg.role === 'assistant' ? (
                        <CaddieHat className="w-3.5 h-3.5 text-gold" />
                      ) : (
                        <User className="w-3.5 h-3.5 text-fairway" />
                      )}
                    </div>
                    <div
                      className={cn(
                        "max-w-[80%] rounded-lg px-3 py-2 text-sm",
                        msg.role === 'assistant'
                          ? "bg-gray-100 text-gray-800"
                          : "bg-gold text-deep-green"
                      )}
                    >
                      <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                    </div>
                  </div>
                );
              })}
              {loading && (
                <div className="flex gap-2">
                  <div className="w-7 h-7 rounded-full bg-gold/20 flex items-center justify-center">
                    <CaddieHat className="w-3.5 h-3.5 text-gold" />
                  </div>
                  <div className="bg-gray-100 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-1.5 text-gray-500 text-sm">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Thinking...
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Quick Questions + Location */}
          {messages.length <= 1 && (
            <div className="px-3 pb-2 space-y-2 shrink-0">
              {locationStatus !== 'granted' && (
                <button
                  onClick={handleShareLocation}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-fairway/10 hover:bg-fairway/20 border border-fairway/20 rounded-lg text-xs text-deep-green font-medium transition-colors"
                  data-testid="caddie-share-location"
                >
                  <MapPin className="w-3.5 h-3.5" />
                  Share My Location for Better Results
                </button>
              )}
              <div className="flex flex-wrap gap-1.5">
                {quickQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(q)}
                    className="px-2.5 py-1 bg-gold/10 hover:bg-gold/20 border border-gold/20 rounded-full text-xs text-deep-green font-medium transition-colors"
                    data-testid={`caddie-widget-quick-${i}`}
                  >
                    <Sparkles className="w-3 h-3 inline mr-1" />
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-gray-200 shrink-0">
            <form onSubmit={handleSubmit} className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask Caddie..."
                className="flex-1 text-sm h-9 border-gray-200"
                disabled={loading}
                data-testid="caddie-widget-input"
              />
              <Button 
                type="submit" 
                size="sm"
                className="btn-gold h-9 px-3"
                disabled={loading || !input.trim()}
                data-testid="caddie-widget-send"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default CaddieWidget;
